import { useState, type ReactElement } from 'react'
import { cn } from '../../utils/cn'

/**
 * What the missing picture was supposed to show. Each kind draws its own
 * artwork, so an empty catalogue still reads as a salon rather than as a grid
 * of grey holes.
 */
export type MediaKind = 'cover' | 'branch' | 'service' | 'person'

/**
 * The artwork is inline SVG rather than a file in public/ for two reasons: it
 * paints from the M3 token variables, so per-vendor theming (epic J) recolours
 * the placeholders along with everything else, and it stays sharp at every size
 * the cards use — 40px review avatars up to a full-bleed hero.
 *
 * SAFE BOX. Every drawing is composed in a 400x400 box that renders with
 * `slice`, i.e. object-cover: the long axis is always cropped. The widest frame
 * in the app is the 21/9 branch banner, which keeps only y 114..286; the
 * tallest is the 4/5 service card, which keeps only x 40..360. So the subject
 * of each drawing lives inside x 44..356 / y 118..282, and only flat washes are
 * allowed to run to the edges. Anything drawn below y≈290 is invisible on half
 * the screens that use it — that is what put the shopfront's door off the
 * bottom of the 16/9 card in the first pass.
 */
const ART: Record<MediaKind, ReactElement> = {
  // Salon interior: an arched alcove between a plant and a low armchair.
  cover: (
    <>
      <rect width="400" height="400" fill="rgb(var(--c-surface-container-low))" />
      <rect y="278" width="400" height="122" fill="rgb(var(--c-tertiary) / 0.1)" />
      <path d="M164 278v-96a36 36 0 0 1 72 0v96Z" fill="rgb(var(--c-primary) / 0.07)" />
      <path
        d="M178 278v-94a22 22 0 0 1 44 0v94"
        fill="none"
        stroke="rgb(var(--c-primary) / 0.16)"
        strokeWidth="2"
      />
      <circle cx="200" cy="196" r="9" fill="rgb(var(--c-tertiary) / 0.26)" />
      {/* Armchair, front on. The arms are what make it read as a chair — without
          them the back and seat alone look like a headstone. */}
      <path d="M272 264v-24a24 24 0 0 1 48 0v24Z" fill="rgb(var(--c-tertiary) / 0.2)" />
      <rect x="264" y="244" width="14" height="30" rx="7" fill="rgb(var(--c-tertiary) / 0.28)" />
      <rect x="314" y="244" width="14" height="30" rx="7" fill="rgb(var(--c-tertiary) / 0.28)" />
      <rect x="264" y="258" width="64" height="16" rx="8" fill="rgb(var(--c-tertiary) / 0.26)" />
      <path d="M94 278h30l-4-40H98Z" fill="rgb(var(--c-tertiary) / 0.24)" />
      <path
        d="M109 238v-46M109 210c-12-5-19-15-19-27 13 1 21 9 23 20M109 216c11-6 17-16 17-28-13 2-21 9-23 21"
        fill="none"
        stroke="rgb(var(--c-primary) / 0.24)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </>
  ),

  // Shopfront: signboard, striped awning, door between two windows.
  branch: (
    <>
      <rect width="400" height="400" fill="rgb(var(--c-surface-container-low))" />
      <rect y="278" width="400" height="122" fill="rgb(var(--c-tertiary) / 0.1)" />
      <rect x="122" y="162" width="156" height="116" fill="rgb(var(--c-primary) / 0.06)" />
      <path d="M110 140h180l-12 24H122Z" fill="rgb(var(--c-primary) / 0.18)" />
      <path
        d="M140 164v-24M170 164v-24M200 164v-24M230 164v-24M260 164v-24"
        stroke="rgb(var(--c-surface-container-low))"
        strokeWidth="6"
      />
      <path d="M182 278v-42a18 18 0 0 1 36 0v42Z" fill="rgb(var(--c-primary) / 0.2)" />
      <rect x="138" y="190" width="34" height="34" rx="4" fill="rgb(var(--c-tertiary) / 0.26)" />
      <rect x="228" y="190" width="34" height="34" rx="4" fill="rgb(var(--c-tertiary) / 0.26)" />
      <rect x="168" y="118" width="64" height="12" rx="6" fill="rgb(var(--c-primary) / 0.16)" />
    </>
  ),

  // Treatment still-life: two bottles beside a sprig.
  service: (
    <>
      <rect width="400" height="400" fill="rgb(var(--c-surface-container-low))" />
      <rect y="278" width="400" height="122" fill="rgb(var(--c-tertiary) / 0.1)" />
      <rect x="142" y="176" width="42" height="102" rx="15" fill="rgb(var(--c-primary) / 0.16)" />
      <rect x="153" y="160" width="20" height="20" rx="6" fill="rgb(var(--c-primary) / 0.22)" />
      <rect x="194" y="206" width="34" height="72" rx="13" fill="rgb(var(--c-tertiary) / 0.26)" />
      <rect x="202" y="193" width="18" height="17" rx="5" fill="rgb(var(--c-tertiary) / 0.32)" />
      <path
        d="M258 278v-56M258 244c-12-5-19-15-19-27 13 1 21 9 23 20M258 250c11-6 17-16 17-28-13 2-21 9-23 21"
        fill="none"
        stroke="rgb(var(--c-primary) / 0.24)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </>
  ),

  // Head and shoulders. Kept small enough that the 4/3 specialist tile still
  // shows the shoulders and not just a floating head.
  person: (
    <>
      <rect width="400" height="400" fill="rgb(var(--c-surface-container-high))" />
      <circle cx="200" cy="178" r="36" fill="rgb(var(--c-primary) / 0.2)" />
      <path d="M120 400v-100a80 80 0 0 1 160 0v100Z" fill="rgb(var(--c-primary) / 0.2)" />
    </>
  ),
}

export interface MediaArtProps {
  kind: MediaKind
  className?: string
}

/** The default artwork on its own — for background layers that take no `<img>`. */
export function MediaArt({ kind, className }: MediaArtProps) {
  return (
    <svg
      viewBox="0 0 400 400"
      preserveAspectRatio="xMidYMid slice"
      role="presentation"
      aria-hidden="true"
      className={cn('h-full w-full', className)}
    >
      {ART[kind]}
    </svg>
  )
}

export interface MediaProps {
  /** The API URL. `null` whenever the salon has not uploaded one. */
  src: string | null | undefined
  /** Empty string marks the picture decorative, e.g. beside a name already in the DOM. */
  alt: string
  kind: MediaKind
  /** Extra classes on the rendered `<img>`/`<svg>`; both already fill their box. */
  className?: string
  /** Above-the-fold pictures pass `eager`; everything else stays lazy. */
  eager?: boolean
}

/**
 * A picture that always renders something. Drops to the artwork when the salon
 * has uploaded nothing *and* when the upload 404s — a dead URL used to leave a
 * broken-image glyph, which looks worse than never having set one.
 *
 * Sits inside the caller's own frame (aspect ratio, rounding, overflow) rather
 * than owning it, because the cards disagree about all three.
 */
export function Media({ src, alt, kind, className, eager = false }: MediaProps) {
  const [failed, setFailed] = useState<string | null>(null)

  if (!src || failed === src) return <MediaArt kind={kind} className={className} />

  return (
    <img
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      onError={() => setFailed(src)}
      className={cn('h-full w-full object-cover', className)}
    />
  )
}
